import { useEffect } from "react";

// In Salesforce Canvas, Abyss SelectInput calls scrollIntoView/focus on portal elements
// when opening. Since the Abyss portal renders near the top of the document body, this
// causes the embedded Canvas iframe to jump to y=0. Suppress both behaviours for any
// element inside .abyss-portal, only in Canvas.
//
// Multiple components call this hook at once (e.g. filters + provider list + a modal all
// mounted together), so the prototype patch is installed once behind a ref count rather
// than per-consumer — otherwise an earlier consumer unmounting would restore the native
// method out from under a still-mounted later consumer.

let scrollIntoViewRefCount = 0;
let originalScrollIntoView: typeof HTMLElement.prototype.scrollIntoView | null = null;

const acquireScrollIntoViewGuard = (): (() => void) => {
  if (scrollIntoViewRefCount === 0) {
    originalScrollIntoView = HTMLElement.prototype.scrollIntoView;
    HTMLElement.prototype.scrollIntoView = function scrollIntoViewOverride(
      ...args: Parameters<typeof HTMLElement.prototype.scrollIntoView>
    ) {
      if (this instanceof HTMLElement && this.closest(".abyss-portal")) {
        return;
      }
      return originalScrollIntoView?.apply(this, args);
    };
  }
  scrollIntoViewRefCount += 1;

  return () => {
    scrollIntoViewRefCount -= 1;
    if (scrollIntoViewRefCount === 0 && originalScrollIntoView) {
      HTMLElement.prototype.scrollIntoView = originalScrollIntoView;
      originalScrollIntoView = null;
    }
  };
};

let focusRefCount = 0;
let originalFocus: typeof HTMLElement.prototype.focus | null = null;

const acquireFocusGuard = (): (() => void) => {
  if (focusRefCount === 0) {
    originalFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function canvasFocusOverride(options?: FocusOptions) {
      if (this instanceof HTMLElement && this.closest(".abyss-portal")) {
        return originalFocus?.call(this, { ...options, preventScroll: true });
      }
      return originalFocus?.call(this, options);
    };
  }
  focusRefCount += 1;

  return () => {
    focusRefCount -= 1;
    if (focusRefCount === 0 && originalFocus) {
      HTMLElement.prototype.focus = originalFocus;
      originalFocus = null;
    }
  };
};

export const useCanvasPortalScrollFix = (canvasIsInSalesforce: boolean) => {
  useEffect(() => {
    if (!canvasIsInSalesforce || typeof HTMLElement === "undefined") {
      return;
    }
    return acquireScrollIntoViewGuard();
  }, [canvasIsInSalesforce]);

  useEffect(() => {
    if (!canvasIsInSalesforce || typeof HTMLElement === "undefined") {
      return;
    }
    return acquireFocusGuard();
  }, [canvasIsInSalesforce]);
};
